import React from 'react';
import { LANDING_GEAR_URL, TRIAGE_ENABLED } from '../constants';

/**
 * Props for the NavBar component.
 */
interface NavBarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onNewRecord: () => void;
  homepageAlertClass?: string;
  /** Number of real pending Triage items (mock preview items are excluded). */
  triagePendingCount?: number;
  /** True while newly-arrived items have not yet been acknowledged by opening Triage. */
  triageHasUnseen?: boolean;
  /** When false, the New Record button is hidden (read-only users). */
  canEdit?: boolean;
}

/**
 * Navigation bar — Homepage, History, Scheduled Events tabs, a Landing Gear
 * link (opens the external LOFT app in a new tab), and the New Record button.
 */
const NavBar: React.FC<NavBarProps> = ({
  activeTab,
  onTabChange,
  onNewRecord,
  homepageAlertClass,
  triagePendingCount = 0,
  triageHasUnseen = false,
  canEdit = true,
}) => {
  /** Opens the external Landing Gear (LOFT) application in a new tab. */
  const openLandingGear = (): void => {
    window.open(LANDING_GEAR_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav className="nav-bar" role="navigation" aria-label="Main navigation">
      <button
        className={`nav-tab ${activeTab === 'homepage' ? 'active' : ''} ${homepageAlertClass || ''}`}
        type="button"
        aria-current={activeTab === 'homepage' ? 'page' : undefined}
        onClick={() => onTabChange('homepage')}
      >
        Homepage
      </button>
      <button
        className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`}
        type="button"
        aria-current={activeTab === 'history' ? 'page' : undefined}
        onClick={() => onTabChange('history')}
      >
        History
      </button>
      <button
        className={`nav-tab ${activeTab === 'scheduled-events' ? 'active' : ''}`}
        type="button"
        aria-current={activeTab === 'scheduled-events' ? 'page' : undefined}
        onClick={() => onTabChange('scheduled-events')}
      >
        Scheduled Events
      </button>
      {TRIAGE_ENABLED && (
        <button
          className={[
            'nav-tab',
            activeTab === 'triage' ? 'active' : '',
            triagePendingCount > 0 ? 'triage-alert-active' : '',
            triageHasUnseen && activeTab !== 'triage' ? 'triage-alert-flash' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          type="button"
          aria-current={activeTab === 'triage' ? 'page' : undefined}
          aria-label={triagePendingCount > 0 ? `Triage, ${triagePendingCount} pending` : 'Triage'}
          onClick={() => onTabChange('triage')}
        >
          Triage
        </button>
      )}
      <button
        className="nav-tab"
        type="button"
        onClick={openLandingGear}
        title="Open Landing Gear (LOFT) in a new tab"
      >
        LANDING GEAR
      </button>
      <div className="nav-spacer" />
      {canEdit && (
        <button className="nav-new-record-btn" type="button" onClick={onNewRecord}>
          New Record
        </button>
      )}
    </nav>
  );
};

export default NavBar;
